import { createSlice } from '@reduxjs/toolkit';

/**
 * Suggested-topics review queue (super-admin).
 *
 * Holds the paginated list, current filters, the open detail row (drawer),
 * and per-row pending-action flags. Approve/reject use optimistic UI: the
 * row is dropped from the list on dispatch, restored on failure.
 */

const defaultFilters = {
  status: 'pending',
  board: '',
  grade: '',
  l1Subject: '',
  chapterName: '',
  search: '',
  page: 1,
  limit: 50,
};

const initialState = {
  rows: [],
  pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
  filters: { ...defaultFilters },
  loading: false,
  error: null,
  selectedRow: null,
  /** ids that have an approve/reject in flight (for spinner/disable UX) */
  pendingActionIds: [],
  /** ids the user has checked for bulk action */
  bulkSelection: [],
  bulkInFlight: false,
  lastBulkResult: null,
};

const removeRowById = (rows, id) => rows.filter((r) => String(r._id) !== String(id));
const upsertRow = (rows, row) => {
  const idx = rows.findIndex((r) => String(r._id) === String(row._id));
  if (idx === -1) return rows;
  const next = rows.slice();
  next[idx] = { ...next[idx], ...row };
  return next;
};

const suggestedTopicsSlice = createSlice({
  name: 'suggestedTopics',
  initialState,
  reducers: {
    fetchStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchSuccess(state, { payload }) {
      state.rows = payload.rows || [];
      state.pagination = payload.pagination || state.pagination;
      state.loading = false;
      state.error = null;
    },
    fetchFailure(state, { payload }) {
      state.loading = false;
      state.error = payload || 'Failed to load suggestions';
    },
    setFilter(state, { payload }) {
      state.filters = { ...state.filters, ...payload, page: 1 };
    },
    setPage(state, { payload }) {
      state.filters.page = payload;
    },
    resetFilters(state) {
      state.filters = { ...defaultFilters };
    },
    openDrawer(state, { payload }) {
      state.selectedRow = payload;
    },
    closeDrawer(state) {
      state.selectedRow = null;
    },
    /** Optimistic removal — row disappears immediately */
    actionStart(state, { payload }) {
      const id = payload;
      if (!state.pendingActionIds.includes(id)) state.pendingActionIds.push(id);
      state.rows = removeRowById(state.rows, id);
      if (state.selectedRow && String(state.selectedRow._id) === String(id)) {
        state.selectedRow = null;
      }
      state.bulkSelection = state.bulkSelection.filter((x) => String(x) !== String(id));
    },
    /** Server confirmed — only clear the in-flight flag */
    actionSuccess(state, { payload }) {
      const id = payload?.id;
      state.pendingActionIds = state.pendingActionIds.filter((x) => x !== id);
    },
    /** Rollback — re-insert the row at the top so the user notices */
    actionFailure(state, { payload }) {
      const { id, row, error } = payload || {};
      state.pendingActionIds = state.pendingActionIds.filter((x) => x !== id);
      if (row) state.rows = [row, ...removeRowById(state.rows, id)];
      state.error = error || 'Action failed';
    },
    toggleBulkSelection(state, { payload }) {
      const id = String(payload);
      if (state.bulkSelection.some((x) => String(x) === id)) {
        state.bulkSelection = state.bulkSelection.filter((x) => String(x) !== id);
      } else {
        state.bulkSelection.push(id);
      }
    },
    clearBulkSelection(state) {
      state.bulkSelection = [];
    },
    bulkStart(state) {
      state.bulkInFlight = true;
      state.lastBulkResult = null;
    },
    bulkFinish(state, { payload }) {
      state.bulkInFlight = false;
      state.lastBulkResult = payload || null;
      // Drop any successfully-approved rows
      const approved = new Set((payload?.approved || []).map(String));
      state.rows = state.rows.filter((r) => !approved.has(String(r._id)));
      state.bulkSelection = [];
    },
    /** Patch a row in place (e.g., after a refresh of a single id) */
    upsertRow(state, { payload }) {
      state.rows = upsertRow(state.rows, payload);
    },
  },
});

export const {
  fetchStart,
  fetchSuccess,
  fetchFailure,
  setFilter,
  setPage,
  resetFilters,
  openDrawer,
  closeDrawer,
  actionStart,
  actionSuccess,
  actionFailure,
  toggleBulkSelection,
  clearBulkSelection,
  bulkStart,
  bulkFinish,
  upsertRow: upsertRowAction,
} = suggestedTopicsSlice.actions;

export default suggestedTopicsSlice.reducer;
